<template>
	<div
		class="neumorphic-button-wrapper"
		:class="[sizeClass, { 'is-disabled': disabled }]"
		:style="styleVars"
		@click="handleClick"
	>
		<svg
			class="neumorphic-button-svg"
			viewBox="0 0 100 100"
			:aria-label="label"
			role="button"
			:aria-disabled="disabled"
		>
			<NeumorphicButtonFilters
				:uid="uid"
				:result-color="resultColor"
			/>

			<g class="button-container">
				<!-- Border circle -->
				<circle
					cx="50"
					cy="50"
					r="45.5"
					class="button-border"
					:class="{ 'is-loading': isLoading }"
					fill="none"
					:stroke="rimStrokeUrl"
					stroke-width="4"
					:filter="borderFilterUrl"
				/>
				<!-- Button circle -->
				<circle
					cx="50"
					cy="50"
					r="43"
					class="button-circle"
					:fill="gradientUrl"
				/>
				<!-- Icon -->
				<foreignObject
					x="22"
					y="22"
					width="56"
					height="56"
				>
					<div
						class="icon-wrapper"
						:class="{ 'is-loading': isLoading }"
						:style="{ color: `var(--icon-color, #a5a5a5)` }"
					>
						<!-- Render icon component if provided -->
						<component v-if="icon" :is="icon" />
						<!-- Otherwise use slot for custom content -->
						<slot v-else />
					</div>
				</foreignObject>
			</g>
		</svg>
	</div>
</template>

<script setup lang="ts">
import type { Component } from 'vue'

const props = defineProps({
	disabled: {
		type: Boolean,
		default: false
	},
	isLoading: {
		type: Boolean,
		default: false
	},
	loadingColor: {
		type: String,
		default: '#3b82f6'
	},
	resultColor: {
		type: [ String, null ],
		default: null
	},
	size: {
		type: String as PropType<'sm' | 'md' | 'lg'>,
		default: 'md'
	},
	label: {
		type: String,
		required: true
	},
	icon: {
		type: [Object, Function] as PropType<Component | null>,
		default: null
	}
})

const emit = defineEmits(['click'])

// Generate unique ID for gradient references
const uid = Math.random().toString(36).substring(2, 9)

const colorMode = useColorMode()

const sizeClass = computed(() => {
	const sizeMap = {
		sm: 'size-sm',
		md: 'size-md',
		lg: 'size-lg'
	}
	return sizeMap[props.size]
})

const gradientUrl = computed(() => {
	const gradientId = colorMode.value === 'dark' ? `buttonGradientDark-${uid}` : `buttonGradient-${uid}`
	return `url(#${gradientId})`
})

const borderFilterUrl = computed(() => {
	const filterId = colorMode.value === 'dark' ? `borderEffectsDark-${uid}` : `borderEffectsLight-${uid}`
	return `url(#${filterId})`
})

const rimStrokeUrl = computed(() => {
	// When loading, use spinning gradient
	if (props.isLoading) {
		return `url(#rimGlowLoading-${uid})`
	}
	// When result color exists (after operation or during cooldown), show solid result color
	if (props.resultColor) {
		return `url(#rimGlowResult-${uid})`
	}
	// Default: static glass color (matches button face in respective mode)
	return colorMode.value === 'dark' ? '#18272B' : '#f8f9f9'
})

const styleVars = computed(() => ({
	'--loading-color': props.loadingColor
}))

const handleClick = (event: MouseEvent) => {
	if (!props.disabled) {
		emit('click', event)
	}
}
</script>

<style scoped>
.neumorphic-button-wrapper {
	display: inline-block;
	cursor: pointer;
	position: relative;
	border-radius: 50%;
	transition: transform 0.2s, box-shadow 0.2s, background-image 0.2s;
	/* Inset shadows for edge highlights - light top-left, dark bottom-right for raised effect */
	/* Extended external shadows for 3D slope effect */
	box-shadow:
		inset -2px -2px 0 rgba(0, 0, 0, 0.15),
		inset 2px 2px 0 rgba(255, 255, 255, 1),
		inset 3px 3px 0 rgba(0, 0, 0, 0.08),
		inset -4px -4px 8px rgba(0, 0, 0, 0.08),
		inset 4px 4px 8px rgba(255, 255, 255, 0.8),
		inset 5px 5px 10px rgba(0, 0, 0, 0.05),
		11px 11px 22px rgba(106, 106, 106, 0.2),
		-11px -11px 22px rgba(255, 255, 255, 0.6),
		6px 6px 12px rgba(106, 106, 106, 0.15),
		-6px -6px 12px rgba(255, 255, 255, 0.5),
		15px 15px 35px rgba(106, 106, 106, 0.1),
		20px 20px 45px rgba(106, 106, 106, 0.06);
}

.neumorphic-button-wrapper::before {
	content: "";
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	border-radius: 50%;
	background-image: linear-gradient(145deg, #ffffff, #F5F5F5);
	z-index: -1;
}

.neumorphic-button-wrapper.is-disabled {
	opacity: 0.5;
	cursor: not-allowed;
	pointer-events: none;
}

.neumorphic-button-wrapper.cursor-not-allowed {
	cursor: not-allowed;
	pointer-events: none;
	animation: cooldownPulse 2s ease-in-out infinite;
}

.neumorphic-button-wrapper.cursor-not-allowed .icon-wrapper {
	color: #9ca3af;
}

@keyframes cooldownPulse {
	0%, 100% {
		opacity: 0.4;
		transform: scale(0.98);
	}
	50% {
		opacity: 0.6;
		transform: scale(1);
	}
}

.neumorphic-button-wrapper:hover:not(.is-disabled):not(.cursor-not-allowed) {
	background-image: linear-gradient(145deg, #ffffff, #E8E8E8);
}

/* Size variants */
.size-sm {
	width: 80px;
	height: 80px;
}

.size-md {
	width: 96px;
	height: 96px;
}

.size-lg {
	width: 112px;
	height: 112px;
}

.neumorphic-button-svg {
	width: 100%;
	height: 100%;
	overflow: visible;
}

.button-container {
	cursor: pointer;
}

.button-circle {
	transition: all 0.2s ease;
}

.icon-wrapper {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: color 0.2s;
	pointer-events: none;
}

.icon-wrapper.is-loading {
	color: var(--loading-color, #3b82f6) !important;
	animation: iconPulse 1s ease-in-out infinite;
}

.icon-wrapper :deep(svg) {
	width: 100%;
	height: 100%;
	stroke: currentColor;
}

@keyframes iconPulse {
	0%, 100% {
		opacity: 1;
	}
	50% {
		opacity: 0.4;
	}
}

/* Dark mode styles */
.dark .neumorphic-button-wrapper {
	box-shadow: inset 2px 2px 0 hsla(195, 4%, 59%, 0.5),
		inset -2px -2px 0 #18272B;
}

.dark .neumorphic-button-wrapper::before {
	background-image: linear-gradient(145deg, #262626, #606060);
	box-shadow: 11px 11px 22px #18272B, -11px -11px 22px hsla(193, 23%, 36%, 0.315);
}

.dark .neumorphic-button-wrapper:hover:not(.is-disabled):not(.cursor-not-allowed) {
	background-image: linear-gradient(145deg, #142024, #2d4349);
}

.dark .neumorphic-button-wrapper.cursor-not-allowed .icon-wrapper {
	color: #6b7280;
}
</style>
